import {
  useAvailableYears,
  useYearlyPerformance,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  monthOf,
  shiftYm,
  ymLabel,
} from '@/entities/performance/lib/performance';
import { Breadcrumbs } from '@/shared/components/ui/breadcrumbs';
import { Icon } from '@/shared/components/ui/icon';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { toUrlYm } from '@/shared/utils/format';
import { Link, useNavigate } from '@tanstack/react-router';
import { dashboardLink } from '../lib';

/**
 * M-09 상단 — Breadcrumbs(실적 › 2026년 7월) + 월 이동 탭(‹ 6월 · 7월 정산 · 8월 ›) — Figma 789:8085
 * 조회 범위 밖(가입 전 · 현재 정산월 이후)으로는 이동할 수 없다.
 * 같은 해는 /yearly 월 목록(현재 정산월까지만 온다), 해를 넘으면 available-years로 판단한다.
 */
export const MonthHeader = ({ ym }: { ym: string }) => {
  const navigate = useNavigate();
  const year = Number(ym.slice(0, 4));
  const { data: years } = useAvailableYears();
  const { data: yearly } = useYearlyPerformance(year);

  const prev = shiftYm(ym, -1);
  const next = shiftYm(ym, 1);
  const canGo = (target: string) => {
    const targetYear = Number(target.slice(0, 4));
    if (targetYear !== year) return !!years?.includes(targetYear);
    return !!yearly?.months.some((m) => m.settlementYearMonth === target);
  };

  // 다른 달로 가면 병의원 선택은 첫 병의원으로 돌아가고 탭은 유지한다
  const go = (target: string) =>
    void navigate({
      to: ROUTE_PATHS.PERFORMANCE_MONTH,
      params: { yearMonth: toUrlYm(target) },
      search: (prevSearch: Record<string, unknown>) => ({
        tab: prevSearch.tab as 'all' | 'images' | 'pharma' | undefined,
      }),
    });

  const step = (target: string, dir: 'prev' | 'next') => {
    const enabled = canGo(target);
    return (
      <button
        type="button"
        disabled={!enabled}
        onClick={() => go(target)}
        aria-label={`${monthOf(target)}월 정산 보기`}
        className="flex items-center gap-1 rounded-8 text-fg-tertiary outline-none hover:text-fg-secondary focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)] disabled:cursor-not-allowed disabled:text-fg-disabled"
      >
        {dir === 'prev' && (
          <Icon
            name="nav-arrow-left-line-16"
            className={cn(enabled ? 'text-fg-light' : 'text-fg-disabled')}
          />
        )}
        <span className="px-1 text-button-18-sb">{monthOf(target)}월</span>
        {dir === 'next' && (
          <Icon
            name="nav-arrow-right-line-16"
            className={cn(enabled ? 'text-fg-light' : 'text-fg-disabled')}
          />
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs
        items={[
          { label: '실적', href: ROUTE_PATHS.PERFORMANCE },
          { label: ymLabel(ym) },
        ]}
        renderLink={(_, children) => (
          <Link {...dashboardLink(ym)}>{children}</Link>
        )}
      />
      <div className="flex w-fit items-center gap-4 rounded-16 bg-surface-white px-3 py-2">
        {step(prev, 'prev')}
        <span className="rounded-12 bg-grey-900 p-3 shadow-100">
          <span className="px-1 text-button-18-sb text-fg-white">
            {monthOf(ym)}월 정산
          </span>
        </span>
        {step(next, 'next')}
      </div>
    </div>
  );
};
